import {
  defineComponent, ref, computed, watch, onBeforeUnmount, onUpdated, nextTick, Teleport, PropType, cloneVNode, VNode, h, provide, inject,
} from 'vue'
import {
  computePosition, offset, shift, flip, arrow,
} from '@floating-ui/dom'
// eslint-disable-next-line import/no-extraneous-dependencies
import { BasePlacement } from '@floating-ui/core'
import type { Options as OffsetOptions } from '@floating-ui/core/src/middleware/offset'
import { DefaultArrow, TooltipArrow } from './assets'
import {
  getRealDomTarget, listenAncientsMutate, listenWindowMutate, listenWindowEvent,
} from './utils'
import { useDebounce } from '../../composables'
import { popoverProvision, PopoverProvision, Children } from './provision'
import { Placement, Trigger } from '../../enums'

export default defineComponent({
  inheritAttrs: false,
  props: {
    target: {
      type: Object as PropType<HTMLElement>,
      default: undefined,
    },
    placement: {
      type: String as PropType<Placement>,
      default: 'bottom',
    },
    offset: {
      type: [Number, Object, Function] as PropType<OffsetOptions>,
      default: undefined,
    },
    arrow: {
      type: Boolean,
      default: false,
    },
    trigger: {
      type: String as PropType<Trigger>,
      default: 'hover',
    },
    visible: {
      type: Boolean,
      default: false,
    },
    validate: {
      type: Function as PropType<() => boolean>,
      default: undefined,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    // prepend all children popovers to body with display none, to help maintain the internal relationship between them
    prepend: {
      type: Boolean,
      default: false,
    },
  },
  emits: {
    change(visible: boolean) {
      return visible !== undefined
    },
    click(e: MouseEvent) {
      return e !== undefined
    },
    'click:popover'(e: MouseEvent) {
      return e !== undefined
    },
    'click:target'(e: MouseEvent) {
      return e !== undefined
    },
    'click:outside'(e: MouseEvent) {
      return e !== undefined
    },
    mousedown(e: MouseEvent) {
      return e !== undefined
    },
    'mousedown:popover'(e: MouseEvent) {
      return e !== undefined
    },
    'mousedown:target'(e: MouseEvent) {
      return e !== undefined
    },
    'mousedown:outside'(e: MouseEvent) {
      return e !== undefined
    },
    mouseup(e: MouseEvent) {
      return e !== undefined
    },
    'mouseup:popover'(e: MouseEvent) {
      return e !== undefined
    },
    'mouseup:target'(e: MouseEvent) {
      return e !== undefined
    },
    'mouseup:outside'(e: MouseEvent) {
      return e !== undefined
    },
  },
  setup(
    props, {
      slots, attrs, emit, expose,
    },
  ) {
    const id = Symbol('popover')

    const ancientPopover = inject<PopoverProvision | undefined>(popoverProvision, undefined)

    const children = ref<Children>(new Map())

    provide<PopoverProvision>(popoverProvision, { children })

    const triggeredShowState = ref(false)
    const showState = ref(false)

    const popover = ref<HTMLElement>()
    const popoverArrow = ref<{ $el: HTMLElement }>()
    const popoverTarget = ref<VNode>()

    const popoverStyle = computed<CSSStyleDeclaration>(
      () => popover.value && getComputedStyle(popover.value),
    )

    const stopListenAncients = ref<() => void>()
    const stopListenWindow = ref<() => void>()
    const stopListenClickOutside = ref<() => void>()

    const position = ref<{ x: number; y: number }>()

    function update(
      targetEl: HTMLElement | undefined,
      popoverEl: HTMLElement | undefined,
      arrowEl: HTMLElement | undefined,
      options: {
        placement: Placement
        offset: OffsetOptions | undefined
        arrow: boolean
      },
    ) {
      if (targetEl && popoverEl) {
        computePosition(
          targetEl,
          popoverEl,
          {
            placement: options.placement,
            middleware: [
              offset(
                options.offset ?? (
                  // eslint-disable-next-line no-nested-ternary
                  options.arrow
                    ? slots.tooltip
                      ? 10
                      : 11
                    : 4
                ),
              ),
              shift(),
              flip(),
              ...arrowEl
                ? [
                  arrow(
                    { element: arrowEl },
                  ),
                ]
                : [],
            ],
          },
        )
          .then(
            ({
              x, y, placement, middlewareData: { arrow: arrowPosition },
            }) => {
              Object
                .assign(
                  popoverEl.style,
                  {
                    top: '0px',
                    left: '0px',
                    transform: `translate(${Math.round(x)}px, ${Math.round(y)}px)`,
                  },
                )

              if (arrowEl) {
                const [basePlacement] = placement.split('-')
                const baseStyle = {
                  top: {
                    bottom: '0px',
                    transform: 'translate(0, 100%)',
                  },
                  right: {
                    left: `-${arrowEl.clientHeight / 2}px`,
                    transform: 'translate(-50%) rotate(90deg)',
                  },
                  bottom: {
                    top: '0px',
                    transform: 'translate(0, -100%) rotate(180deg)',
                  },
                  left: {
                    right: `-${arrowEl.clientHeight / 2}px`,
                    transform: 'translate(50%) rotate(-90deg)',
                  },
                }

                Object.assign(
                  arrowEl.style,
                  {
                    top: null,
                    right: null,
                    bottom: null,
                    left: null,
                    ...arrowPosition?.y !== undefined && {
                      top: `${arrowPosition.y}px`,
                    },
                    ...arrowPosition?.x !== undefined && {
                      left: `${arrowPosition.x}px`,
                    },
                    ...baseStyle[basePlacement as BasePlacement],
                  },
                )
              }

              // redundant updates to ensure popover is positioned correctly
              if (position.value?.x !== x || position.value?.y !== y) {
                position.value = { x, y }

                nextTick(
                  () => update(targetEl, popoverEl, arrowEl, options),
                )
              }
            },
          )
          .then(
            () => children.value
              ?.forEach(
                child => child.update(),
              ),
          )
      }
    }

    const changeShowState = useDebounce(
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      (v: boolean, options: { immediate: boolean; force: boolean }) => {
        if (v || !children.value.size) {
          showState.value = v
        } else if (options.force) {
          showState.value = triggeredShowState.value

          children.value
            .forEach(
              child => child.changeShowState(triggeredShowState.value, options),
            )
        } else {
          const stop = watch(
            children,
            map => {
              if (map.size === 0) {
                stop()
                showState.value = triggeredShowState.value
              }
            },
            { deep: true },
          )
        }
      },
      { immediate: (_, { immediate }) => immediate },
    )

    function showPopover(v: boolean, { immediate = !!slots.tooltip, force = false }: { immediate?: boolean; force?: boolean } = {}) {
      if (!props.disabled) {
        if (!v || (!props.validate || props.validate())) {
          triggeredShowState.value = v
          changeShowState(v, { immediate, force })
        }
      }
    }

    function onMouseenter() {
      showPopover(true)
    }

    function onMouseleave() {
      showPopover(false)
    }

    function onClick() {
      showPopover(true)
    }

    function targetOnClick() {
      showPopover(!triggeredShowState.value)
    }

    const bindings = {
      ...(props.trigger === 'hover') && {
        onMouseenter,
        onMouseleave,
      },
      ...(props.trigger === 'click') && {
        onClick,
      },
    }

    const updateContext = ref<Parameters<typeof update>>()

    watch(
      [
        () => props.target,
        () => props.trigger,
      ],
      ([target, trigger]) => {
        if (target) {
          target.removeEventListener('mouseenter', onMouseenter)
          target.removeEventListener('mouseleave', onMouseleave)
          target.removeEventListener('click', targetOnClick)

          if (trigger === 'hover') {
            target.addEventListener('mouseenter', onMouseenter)
            target.addEventListener('mouseleave', onMouseleave)
          }

          if (trigger === 'click') {
            target.addEventListener('click', targetOnClick)
          }
        }
      },
      { immediate: true },
    )

    watch(
      [
        showState,
        popover,
        popoverArrow,
        () => props.placement,
        () => props.offset,
        () => props.arrow,
      ],
      ([show, p, a, pp, po, pa], [prevShow]) => {
        stopListenAncients.value?.()
        stopListenWindow.value?.()
        stopListenClickOutside.value?.()

        // when popover mounted, try to update it's position lazily
        if (show && p) {
          // when we have a target VNode or target prop
          if (popoverTarget.value || props.target) {
            // get the real dom from target VNode for popover and update the position of popover
            const el = popoverTarget.value
              ? getRealDomTarget(popoverTarget.value)
              : props.target

            if (el) {
              const args: Parameters<typeof update> = [
                el,
                p,
                a?.$el,
                {
                  placement: pp,
                  offset: po,
                  arrow: pa,
                },
              ]

              nextTick(
                () => update(...args),
              )

              // listen to the scroll event of ancient element, in order to update the position of popover lazily
              stopListenAncients.value = listenAncientsMutate(
                el,
                () => update(...args),
              )

              // listen to the resize event of window, in order to update the position of popover lazily
              stopListenWindow.value = listenWindowMutate(
                () => update(...args),
              )

              if (props.trigger === 'click' || props.trigger === 'manual') {
                stopListenClickOutside.value = listenWindowEvent(
                  {
                    popover: p,
                    target: el,
                    children,
                  },
                  {
                    onClickPopover: e => {
                      emit('click', e)
                      emit('click:popover', e)
                    },
                    onClickTarget: e => {
                      emit('click', e)
                      emit('click:target', e)
                    },
                    onClickOutside: e => {
                      if (props.trigger === 'click') {
                        showPopover(false)
                      }

                      emit('click:outside', e)
                    },
                    onMousedownPopover: e => {
                      emit('mousedown', e)
                      emit('mousedown:popover', e)
                    },
                    onMousedownTarget: e => {
                      emit('mousedown', e)
                      emit('mousedown:target', e)
                    },
                    onMousedownOutside: e => {
                      emit('mousedown:outside', e)
                    },
                    onMouseupPopover: e => {
                      emit('mouseup', e)
                      emit('mouseup:popover', e)
                    },
                    onMouseupTarget: e => {
                      emit('mouseup', e)
                      emit('mouseup:target', e)
                    },
                    onMouseupOutside: e => {
                      emit('mouseup:outside', e)
                    },
                  },
                )
              }

              // increase the children of it's ancient popover when popover show
              if (ancientPopover) {
                ancientPopover.children.value
                  .set(
                    id,
                    {
                      popover: { el: p, children: children.value },
                      update: () => update(...args),
                      changeShowState,
                    },
                  )
              }

              updateContext.value = args
            }
          }
        // reduce the children of it's ancient popover when popover gone
        } else if (ancientPopover) {
          ancientPopover.children.value.delete(id)
        }

        if (prevShow !== show) {
          emit('change', show)
        }
      },
    )

    if (props.trigger === 'manual') {
      watch(
        () => props.visible,
        v => {
          showPopover(v, { immediate: true, force: true })
        },
        { immediate: true },
      )
    }

    onBeforeUnmount(
      () => {
        if (ancientPopover) {
          ancientPopover.children.value.delete(id)
        }
      },
    )

    function redundantUpdate() {
      nextTick(
        () => {
          if (updateContext.value) {
            update(...updateContext.value)
          }
        },
      )
    }

    onUpdated(
      // some components use nextTick for reflow, so it's needed to use nextTick in nextTick to keep popover updated in order
      () => nextTick(redundantUpdate),
    )

    expose({
      updatePosition: redundantUpdate,
    })

    return () => <>
      {
        slots
          .default?.()
          .map(
            (slot, i) => {
              // locate popover with first element
              if (i === 0) {
                const first = cloneVNode(
                  slot,
                  {
                    ...bindings,
                    ...props.trigger === 'click' && { onClick: targetOnClick },
                  },
                )

                popoverTarget.value = first

                return first
              }

              return slot
            },
          )
      }
      {
        (slots.popover || slots.tooltip || slots.content) && (
          props.prepend
            ? <Teleport to="body">
              <div
                v-show={showState.value}
                ref={popover}
                class={
                  [
                    ...(
                    // eslint-disable-next-line no-nested-ternary
                      slots.popover
                        ? ['d-popover']
                        : slots.tooltip
                          ? ['d-popover', 'd-popover-tooltip', 'd-text', '--color-white']
                          : ['d-popover', 'd-popover-default']
                    ),
                    attrs.class,
                  ]
                }
                style={attrs.style}
                {...bindings}
              >
                {slots.popover?.() || slots.tooltip?.() || slots.content?.()}
                {
                  props.arrow && (
                    slots.tooltip
                      ? h(
                        TooltipArrow,
                        {
                          ref: popoverArrow,
                          ...popoverStyle.value && {
                            backgroundColor: popoverStyle.value.backgroundColor,
                          },
                        },
                      )
                      : h(
                        DefaultArrow,
                        {
                          ref: popoverArrow,
                          ...popoverStyle.value && {
                            borderColor: popoverStyle.value.borderColor,
                            backgroundColor: popoverStyle.value.backgroundColor,
                          },
                        },
                      )
                  )
                }
              </div>
            </Teleport>
            : showState.value && <Teleport to="body">
              <div
                ref={popover}
                class={
                  [
                    ...(
                    // eslint-disable-next-line no-nested-ternary
                      slots.popover
                        ? ['d-popover']
                        : slots.tooltip
                          ? ['d-popover', 'd-popover-tooltip', 'd-text', '--color-white']
                          : ['d-popover', 'd-popover-default']
                    ),
                    attrs.class,
                  ]
                }
                style={attrs.style}
                {...bindings}
              >
                {slots.popover?.() || slots.tooltip?.() || slots.content?.()}
                {
                  props.arrow && (
                    slots.tooltip
                      ? h(
                        TooltipArrow,
                        {
                          ref: popoverArrow,
                          ...popoverStyle.value && {
                            backgroundColor: popoverStyle.value.backgroundColor,
                          },
                        },
                      )
                      : h(
                        DefaultArrow,
                        {
                          ref: popoverArrow,
                          ...popoverStyle.value && {
                            borderColor: popoverStyle.value.borderColor,
                            backgroundColor: popoverStyle.value.backgroundColor,
                          },
                        },
                      )
                  )
                }
              </div>
            </Teleport>
        )
      }
    </>
  },
})
